import {
  Appointment,
  QUEUE_TYPE,
  TagRecord,
  TagRecordWithCount,
} from "@elixir/types";
import { useEffect, useMemo, useState } from "react";
import { Chip, OptionsSelectUi } from "ui";
import useGetQueueConfig from "../../../../apis/tryst/get-queue-config";
import usePostTagAnAppointment from "../../../../apis/tryst/post-tag-an-appointment";
import TagsPopup from "../../../cards/queue/tags-popup";
import { getQueueTypeFromKey } from "@elixir/utils";
import { TagRegularIcon } from "@elixir/icons";
import { Popover, Tooltip } from "antd";

const ShowSelectedTags = ({
  apt,
  className,
  queueType,
  numberOfChipsToShow = 1,
  popoverClassname,
}: {
  apt: Appointment;
  className?: string;
  queueType: QUEUE_TYPE | string;
  numberOfChipsToShow?: number;
  popoverClassname?: string;
}) => {
  const { data } = useGetQueueConfig();
  const tags = data?.data?.tags || [];
  const oldTags = data?.data?.old_tags || [];
  const { tags: aptTags = [], start_time, aid } = apt;
  const [allTags, setAllTags] = useState<TagRecordWithCount[]>([]);
  const { mutateAsync: toggleTagSelection } = usePostTagAnAppointment({
    date: start_time,
    clinicId: apt.clinic_id,
    queueType: getQueueTypeFromKey(queueType),
  });

  const [selectedTags, setSelectedTags] = useState<TagRecord["tag_id"][]>(
    () => aptTags || []
  );

  const handleTagClick = async (tagId: TagRecord["tag_id"]) => {
    const isPresent = selectedTags.includes(tagId);
    let updatedTags: (string | number)[] = [];
    if (isPresent) {
      updatedTags = selectedTags.filter((id) => id !== tagId);
    } else {
      updatedTags = [...selectedTags, tagId];
    }

    setSelectedTags(updatedTags);

    toggleTagSelection({
      appointment: { aid },
      tagId,
      updatedTags: updatedTags,
    });
  };

  useEffect(() => {
    const oldTagsPresentInAptTags = oldTags.filter((tag) =>
      selectedTags.some(
        (aptTag) => tag?.tag_id?.toString() === aptTag?.toString()
      )
    );
    setAllTags([...tags, ...oldTagsPresentInAptTags]);
  }, [selectedTags]);

  const selectedTagRecords = useMemo(
    () =>
      allTags.filter((tag) =>
        selectedTags.some((id) => tag.tag_id?.toString() === id?.toString())
      ),
    [allTags, selectedTags]
  );

  const [showTagsPopup, setShowTagsPopup] = useState<boolean>(false);

  const hoverTooltipTitle =
    selectedTagRecords.length > 0 ? (
      <div className="dr-flex dr-flex-wrap dr-gap-4 dr-p-8 dr-bg-darwin-neutral-0 dr-rounded-8 dr-shadow-elevation-100 dr-max-w-[214px]">
        {selectedTagRecords.map((tag) => (
          <Chip
            key={tag.tag_id}
            title={tag.label}
            borderRadius="dr-rounded-2 dr-border dr-border-1"
            paddingClassName="dr-py-3 dr-px-6"
            className="CalloutRegular dr-line-clamp-1"
            customStyles={{
              ...(tag.bg_hex ? { backgroundColor: tag.bg_hex } : {}),
              ...(tag.hex ? { color: tag.hex, borderColor: tag.hex } : {}),
            }}
            titleClassName="dr-line-clamp-1"
          />
        ))}
      </div>
    ) : null;

  const triggerContent = (
    <>
      <OptionsSelectUi
        Icon={
          <TagRegularIcon
            className={`dr-w-16 dr-min-w-16 ${
              selectedTags.length === 0
                ? "dr-text-darwin-primary-primary"
                : "dr-text-darwin-neutral-400"
            }`}
          />
        }
        title={"Add Tags"}
        options={allTags.map((tag) => ({
          option_id: tag.tag_id.toString(),
          label: tag.label,
          hex: tag.hex,
          bg_hex: tag.bg_hex,
        }))}
        selectedOptions={selectedTags.map((tag) => tag.toString())}
        extraClass={className}
        numberOfChipsToShow={numberOfChipsToShow}
      />
    </>
  );

  return (
    <Popover
      placement="rightTop"
      trigger="click"
      className={`dr-cursor-pointer ${popoverClassname || ""}`}
      open={showTagsPopup}
      onOpenChange={(value) => setShowTagsPopup(value)}
      content={
        <TagsPopup
          onClose={() => setShowTagsPopup(false)}
          onTagSelect={handleTagClick}
          tags={allTags}
          selectedTags={selectedTags}
          key={allTags.length}
        />
      }
      destroyTooltipOnHide={true}
      overlayClassName="fd-popup"
    >
      {hoverTooltipTitle ? (
        <Tooltip
          trigger="hover"
          title={hoverTooltipTitle}
          placement="top"
          overlayClassName="dr-tags-tooltip dr-tags-tooltip--transparent"
          overlayInnerStyle={{ background: "transparent", boxShadow: "none" }}
          destroyTooltipOnHide
        >
          <span className="dr-inline-block">{triggerContent}</span>
        </Tooltip>
      ) : (
        triggerContent
      )}
    </Popover>
  );
};

export default ShowSelectedTags;
